import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EVENT_CARD_HEIGHT, TabTypes } from "../../../helpers/constants";
import { IAppState } from "../../../helpers/store";
import {
  failedFirebaseDashboard,
  isHardEvent,
  removeCommunityEvent,
} from "../dashboardSlice";
import NotificationEvent from "../NotificationEvent";
import { EventStatus } from "../../../helpers/constants";
import { isPausedEvent } from "../CommunityUnit";
import "./index.css";
import { dataPaths, listenToListChanges } from "../../../helpers/firebase";
import { IEvent } from "../../../services/dashboard.services";
import useOrganization from "./hooks/useOrganization";
import { VariableSizeList as List } from "react-window";

export default function CriticalNotifications(props: IProps) {
  const { addTab, unitId, organization, events, height, width } = props;
  /* ---- Hooks ---- */
  const dispatch = useDispatch();
  const { selectedOrganizationID, orgsCommunities } = useOrganization();

  /* ---- Selectors ---- */
  const firebaseAuth = useSelector(
    (state: IAppState) => state.loginState.firebaseAuth
  );
  // The change feed removes events itself, these listeners only run without it
  const listenersOn = useSelector(
    (state: IAppState) => state.dashboardState.changeFeed.mode === "off"
  );
  const communityEvents = useSelector(
    (state: IAppState) => state.dashboardState.communityEvents
  );
  const selectedCommunityID = useSelector(
    (state: IAppState) => state.headerState.selectedCommunity?.id
  );

  let criticalEvents =
    communityEvents &&
    communityEvents
      .filter((eventEl) => unitId === null || eventEl.unit_id === unitId)
      .filter((eventEl) => isHardEvent(eventEl) && !isPausedEvent(eventEl))
      .filter(
        (eventEl) =>
          eventEl.status === EventStatus.UNASSIGNED ||
          eventEl.status === EventStatus.ASSIGNED
      );

  if (organization && events) {
    criticalEvents = events.filter((eventEl) => isHardEvent(eventEl));
  }

  /* Effects */
  // Listens to removed childs from the community unresolved events node and marks them as solved
  useEffect(() => {
    if (selectedCommunityID !== undefined && firebaseAuth && listenersOn) {
      return listenToListChanges(
        dataPaths.unresolvedEvents(selectedCommunityID),
        {
          onRemoved: (resolvedEvent) => {
            if (resolvedEvent === null) return;
            dispatch(removeCommunityEvent(resolvedEvent.id));
          },
          onError: () => dispatch(failedFirebaseDashboard()),
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommunityID, firebaseAuth, listenersOn]);

  // Listens to removed childs from the communities unresolved events node and marks them as solved
  useEffect(() => {
    if (
      selectedOrganizationID !== undefined &&
      orgsCommunities !== undefined &&
      firebaseAuth &&
      listenersOn
    ) {
      const unsubscribes = orgsCommunities.map((community) =>
        listenToListChanges(dataPaths.unresolvedEvents(community.id), {
          onRemoved: (resolvedEvent) => {
            if (resolvedEvent === null) return;
            dispatch(removeCommunityEvent(resolvedEvent.id));
          },
          onError: () => dispatch(failedFirebaseDashboard()),
        })
      );
      return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganizationID, firebaseAuth, listenersOn]);

  const getItemSize = (index: number) => EVENT_CARD_HEIGHT + 5;

  const Row = <T extends { index: number; style: any }>(props: T) => {
    if (criticalEvents === undefined) return null;
    const criticalEvent = criticalEvents[props.index];
    return (
      <div style={props.style}>
        <NotificationEvent
          id={criticalEvent.id}
          unit_id={criticalEvent.unit_id}
          event_type={criticalEvent.event_type}
          responder_id={criticalEvent.responder_id}
          status={criticalEvent.status}
          data={criticalEvent.data}
          time_created={criticalEvent.time_created}
          notification_type={criticalEvent.notification_type}
          community_id={criticalEvent.community_id}
          read_status={criticalEvent.read_status}
          onClickOpenTab={addTab}
        />
      </div>
    );
  };

  return (
    <List
      height={height}
      itemCount={criticalEvents?.length || 0}
      itemSize={getItemSize}
      width={width}
    >
      {Row}
    </List>
  );
}

interface IProps {
  addTab: (id: string, type: TabTypes) => void;
  unitId: string | null;
  organization?: boolean;
  events?: IEvent[];
  height: number;
  width: number;
}
